interface KartraLead {
  status: string;
  lead_details?: {
    memberships?: KartraMembership[];
  };
  message?: string;
}

interface KartraMembership {
  name: string;
  level_name: string;
  active: string;
  created: string;
  expires: string;
}

interface MembershipDetails {
  hasActiveMembership: boolean;
  memberships: KartraMembership[];
  planLevel: string | null;
  highestPlan: KartraMembership | null;
}

type MembershipStatus = 'active' | 'inactive' | 'unknown';

class KartraClientService {
  private readonly apiUrl = 'https://app.kartra.com/api';
  private readonly apiKey = process.env.REACT_APP_KARTRA_API_KEY;
  private readonly appId = process.env.REACT_APP_KARTRA_APP_ID;
  private readonly apiPassword = process.env.REACT_APP_KARTRA_API_PASSWORD;

  constructor() {
    if (!this.apiKey || !this.appId || !this.apiPassword) {
      console.warn(
        '[KartraClient] Missing environment variables. Set REACT_APP_KARTRA_API_KEY, REACT_APP_KARTRA_APP_ID, and REACT_APP_KARTRA_API_PASSWORD',
      );
    }
  }

  isConfigured(): boolean {
    return !!(this.apiKey && this.appId && this.apiPassword);
  }

  async getLead(email: string): Promise<KartraLead | null> {
    if (!this.isConfigured()) {
      console.error(
        '[KartraClient] Service not configured - missing environment variables',
      );
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('api_key', this.apiKey!);
      formData.append('app_id', this.appId!);
      formData.append('api_password', this.apiPassword!);
      formData.append('get_lead[email]', email);

      // Add a timeout to avoid hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000);

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: KartraLead = await response.json();
      console.log('[KartraClient] API Response:', data);

      return data;
    } catch (error) {
      console.error('[KartraClient] Error fetching lead:', error);
      return null;
    }
  }

  async hasActiveMembership(email: string): Promise<boolean> {
    const status = await this.checkMembershipStatus(email);
    return status === 'active';
  }

  async getPlanLevel(email: string): Promise<string | null> {
    const lead = await this.getLead(email);

    if (!lead || lead.status !== 'Success' || !lead.lead_details?.memberships) {
      return null;
    }

    const levelPriority: Record<string, number> = {
      'Business Annual': 4,
      'Business Monthly': 3,
      Annual: 2,
      Standard: 1,
    };

    let highestLevel: string | null = null;
    let highestPriority = 0;

    for (const membership of lead.lead_details.memberships) {
      const priority = levelPriority[membership.level_name];
      if (
        membership.name === 'Crate Hackers' &&
        membership.active === '1' &&
        priority &&
        priority > highestPriority
      ) {
        highestLevel = membership.level_name;
        highestPriority = priority;
      }
    }

    return highestLevel;
  }

  async getMembershipDetails(email: string): Promise<MembershipDetails> {
    const lead = await this.getLead(email);

    const defaultResult: MembershipDetails = {
      hasActiveMembership: false,
      memberships: [],
      planLevel: null,
      highestPlan: null,
    };

    if (!lead || lead.status !== 'Success' || !lead.lead_details?.memberships) {
      return defaultResult;
    }

    // Filter to only Crate Hackers memberships
    const crateMemberships = lead.lead_details.memberships.filter(
      (membership) => membership.name === 'Crate Hackers',
    );

    // Find active memberships
    const activeMemberships = crateMemberships.filter(
      (membership) =>
        membership.active === '1' &&
        ['Annual', 'Standard', 'Business Monthly', 'Business Annual'].includes(
          membership.level_name,
        ),
    );

    // Find highest priority active membership
    const levelPriority: Record<string, number> = {
      'Business Annual': 4,
      'Business Monthly': 3,
      Annual: 2,
      Standard: 1,
    };

    let highestPlan: KartraMembership | null = null;
    let highestPriority = 0;

    for (const membership of activeMemberships) {
      const priority = levelPriority[membership.level_name];
      if (priority && priority > highestPriority) {
        highestPlan = membership;
        highestPriority = priority;
      }
    }

    return {
      hasActiveMembership: activeMemberships.length > 0,
      memberships: crateMemberships,
      planLevel: highestPlan?.level_name || null,
      highestPlan,
    };
  }

  /**
   * Return tri-state membership status for Crate Hackers
   * - 'active': confirmed active membership
   * - 'inactive': confirmed no active membership (based on successful API response)
   * - 'unknown': network/API error or inconclusive response
   */
  async checkMembershipStatus(email: string): Promise<MembershipStatus> {
    const lead = await this.getLead(email);

    // Any transport/config failure → unknown
    if (!lead) {
      return 'unknown';
    }

    // Be conservative: only decide inactive when we have a successful payload
    if (lead.status !== 'Success' || !lead.lead_details?.memberships) {
      return 'unknown';
    }

    const isActive = lead.lead_details.memberships.some(
      (membership) =>
        membership.name === 'Crate Hackers' &&
        ['Annual', 'Standard', 'Business Monthly', 'Business Annual'].includes(
          membership.level_name,
        ) &&
        membership.active === '1',
    );

    return isActive ? 'active' : 'inactive';
  }

  private handleNoActiveMembership() {
    alert(
      'Your Crate Hackers membership has expired or is no longer active. You will be logged out.',
    );

    // Import dynamically to avoid circular dependencies
    import('../utils/logout').then(({ logout }) => {
      logout('membership_expired');
    });
  }

  async checkMembershipAndLogout(
    email: string,
    autoLogout = false,
  ): Promise<boolean> {
    try {
      const status = await this.checkMembershipStatus(email);

      if (status === 'inactive' && autoLogout) {
        console.log(
          '[KartraClient] Confirmed inactive membership, logging out user',
        );
        this.handleNoActiveMembership();
        return false;
      }

      // Treat 'unknown' conservatively (do not logout here)
      return status === 'active';
    } catch (error) {
      console.error('[KartraClient] Error checking membership:', error);
      // Never force logout on error here; leave decision to caller
      return false;
    }
  }
}

export default new KartraClientService();
